import { useState, useCallback } from 'react';
import { Box } from '@mui/material';
import { SidebarMenu } from './SidebarMenu';
import DashboardHeader from './header/DashboardHeader';
import { UserDialogProvider } from './user-dialog/UserDialogContext';
import { WithUnhappyStates } from '../pages/environments/WithUnhappyStates';
import useWhenInitialFetchDone from './useWhenInitialFetchDone';
import usePaywallTrialCarousel from './header/usePaywallTrialCarousel';
import { DASHBOARD_HEADER_HEIGHT_PX } from '../../constants';
import { useExpandedDocumentationForFreeUsers } from './useExpandedDocumentationForFreeUsers';
import { WebhooksProvider } from './webhooks/WebhooksContext';
import useAiAgentCenter from '../aiAgentCenter/useAiAgentCenter';
import AiAgentCenterPanel from '../aiAgentCenter/AiAgentCenterPanel';

interface DashboardProps {
  children?: React.ReactNode;
}

const Dashboard = ({ children }: DashboardProps) => {
  const [open, setOpen] = useState(true);
  const { toggleIsOpened, isOpened } = useAiAgentCenter();

  useWhenInitialFetchDone();
  usePaywallTrialCarousel();
  useExpandedDocumentationForFreeUsers();

  const toggleHandler = () => {
    setOpen(!open);
  };

  const setSideMenuOpen = useCallback((isOpen: boolean) => {
    setOpen(isOpen);
  }, []);

  return (
    <Box sx={{ overflowX: 'hidden' }}>
      <DashboardHeader sidebarMenuOpen={open} toggle={toggleHandler} />
      <Box sx={{ display: 'flex', height: `calc(100vh - ${DASHBOARD_HEADER_HEIGHT_PX})` }}>
        {/*This location is selected to make the "Agent Center" in expanded mode is behind the sidebar */}
        <AiAgentCenterPanel closePanel={toggleIsOpened} isOpened={isOpened} />
        <SidebarMenu open={open} />
        <Box component="main" sx={{ flexGrow: 1, overflow: 'auto' }}>
          <WithUnhappyStates setSideMenuOpen={setSideMenuOpen}>{children}</WithUnhappyStates>
        </Box>
      </Box>
    </Box>
  );
};

const DashboardWithContext = (props: DashboardProps) => {
  return (
    <UserDialogProvider>
      <WebhooksProvider>
        <Dashboard {...props} />
      </WebhooksProvider>
    </UserDialogProvider>
  );
};

export { DashboardWithContext as Dashboard };
